import {getFormProps, getInputProps, useForm} from '@conform-to/react'
import {parseWithZod} from '@conform-to/zod'
import {useFetcher} from '@remix-run/react'
import {Button, TextField} from '@translated/babele-react'
import React, {useEffect} from 'react'

import {type Memory} from '~/models/memory/memory.server'
import {Dialog, DialogContent, DialogTrigger} from '~/ui/Modal'
import {type UpdateMemoryAction, updateMemorySchema} from '../_action.memory'

export const UpdateMemoryDialog = ({
  memory,
  children,
}: {
  memory: Memory
  children: React.ReactNode
}) => {
  const [isUpdateMemoryModalOpen, setIsUpdateMemoryModalOpen] =
    React.useState(false)

  return (
    <Dialog
      open={isUpdateMemoryModalOpen}
      onOpenChange={setIsUpdateMemoryModalOpen}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <UpdateMemoryForm
          onSuccess={() => setIsUpdateMemoryModalOpen(false)}
          memory={memory}
        />
      </DialogContent>
    </Dialog>
  )
}

function UpdateMemoryForm({
  onSuccess,
  memory,
}: {
  onSuccess?: () => void
  memory: Memory
}) {
  const fetcher = useFetcher<UpdateMemoryAction>()
  const [form, fields] = useForm({
    onValidate: ({formData}) => {
      return parseWithZod(formData, {schema: updateMemorySchema})
    },
    lastResult: fetcher.data,
    id: 'update-memory',
    defaultValue: {name: memory.name, id: memory.id.toString()},
  })

  const isSuccess = fetcher.data?.status == 'success'

  useEffect(() => {
    if (isSuccess) onSuccess?.()
  }, [isSuccess, onSuccess])

  return (
    <fetcher.Form {...getFormProps(form)} action="/memory" method="POST">
      <div className="flex flex-col gap-8 py-6">
        <h2 className="text-display-md text-center">Rename memory</h2>
        <div className="flex flex-col gap-4">
          <TextField
            {...getInputProps(fields.name, {type: 'text'})}
            aria-label="Memory name"
            placeholder="Memory name"
          />
          {form.errors && (
            <div className="p-4 bg-critical-100 text-critical-800">
              {form.errors}
            </div>
          )}
          <input {...getInputProps(fields.id, {type: 'hidden'})} />
          <Button
            isLoading={fetcher.state != 'idle'}
            variant={{tone: 'primary'}}
            type="submit"
            name="_action"
            value="updateMemory"
          >
            Rename
          </Button>
        </div>
      </div>
    </fetcher.Form>
  )
}
